// VORNX — Single source of client-side tracking.
// Loads ONLY after explicit cookie consent (GDPR) AND only if IDs are configured
// via environment variables. With no IDs set, every function is a safe no-op.
//
// When the agency provides the accounts, add to frontend/.env:
//   REACT_APP_GA_ID=G-XXXXXXX            (Google Analytics 4)
//   REACT_APP_GTM_ID=GTM-XXXXXXX         (Google Tag Manager)
//   REACT_APP_META_PIXEL_ID=XXXXXXXXXXX  (Meta Pixel)
// No code change needed — paste the IDs and redeploy.

const GA_ID = process.env.REACT_APP_GA_ID;
const GTM_ID = process.env.REACT_APP_GTM_ID;
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const CONSENT_KEY = "vornx_cookie_consent";

// First-party funnel collector (no Google Data API). Mirrors the GA4 ecommerce
// events to our own backend so the Action Center can show a real funnel.
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const FUNNEL_EVENTS = ["view_item", "add_to_cart", "begin_checkout", "add_payment_info", "purchase"];

const sessionId = () => {
  try {
    let s = sessionStorage.getItem("vornx_sid");
    if (!s) {
      s = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("vornx_sid", s);
    }
    return s;
  } catch {
    return null;
  }
};

const collectFirstParty = (name, params = {}) => {
  if (!API_BASE || !FUNNEL_EVENTS.includes(name)) return;
  try {
    fetch(`${API_BASE}/api/analytics/collect`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        event: name,
        value: params.value,
        currency: params.currency || "EUR",
        transaction_id: params.transaction_id || null,
        payment_type: params.payment_type || null,
        session_id: sessionId(),
        items: params.items || [],
      }),
    }).catch(() => {});
  } catch {
    /* fire-and-forget */
  }
};

// --- Live visitors (first-party presence) -------------------------------------
let activeProduct = null;
let presenceTimer = null;
let currentPath = typeof window !== "undefined" ? window.location.pathname : "/";

// Set by the product page so the heartbeat can report the SKU being viewed.
export const setActiveProduct = (p) => {
  activeProduct = p ? { sku: p.sku || null, name: p.name } : null;
};

const sendHeartbeat = () => {
  if (!initialized || !API_BASE) return;
  try {
    fetch(`${API_BASE}/api/analytics/heartbeat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        session_id: sessionId(),
        path: currentPath,
        product_sku: activeProduct?.sku || null,
        product_name: activeProduct ? resolveName(activeProduct.name) : null,
        locale: (typeof navigator !== "undefined" && navigator.language) || "",
      }),
    }).catch(() => {});
  } catch {
    /* fire-and-forget */
  }
};

export const startPresence = () => {
  if (!initialized) return;
  sendHeartbeat();
  if (presenceTimer) return;
  presenceTimer = setInterval(sendHeartbeat, 15000);
};

export const updatePresencePath = (path) => {
  currentPath = path || "/";
  if (initialized) sendHeartbeat();
};

let initialized = false;

const hasConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw).choice === "accept" : false;
  } catch {
    return false;
  }
};

const loadGA = () => {
  if (!GA_ID || document.getElementById("ga-src")) return;
  const s = document.createElement("script");
  s.id = "ga-src";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { anonymize_ip: true });
};

const loadGTM = () => {
  if (!GTM_ID || document.getElementById("gtm-src")) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const s = document.createElement("script");
  s.id = "gtm-src";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);
};

const loadPixel = () => {
  if (!PIXEL_ID || window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", PIXEL_ID);
  window.fbq("track", "PageView");
};

export const initAnalytics = () => {
  if (initialized || (!GA_ID && !PIXEL_ID && !GTM_ID)) return;
  initialized = true;
  loadGTM();
  loadGA();
  loadPixel();
};

export const initAnalyticsIfConsented = () => {
  if (hasConsent()) initAnalytics();
};

export const trackPageview = (path) => {
  if (!initialized) return;
  if (window.gtag && GA_ID) window.gtag("config", GA_ID, { page_path: path });
  if (window.fbq) window.fbq("track", "PageView");
};

// Generic conversion event. Maps a normalized VORNX event to GA4 + Meta Pixel.
// Standard names: "view_item", "add_to_cart", "begin_checkout", "purchase".
const PIXEL_MAP = {
  view_item: "ViewContent",
  add_to_cart: "AddToCart",
  begin_checkout: "InitiateCheckout",
  purchase: "Purchase",
};

export const trackEvent = (name, params = {}) => {
  if (!initialized) return;
  if (window.gtag && GA_ID) window.gtag("event", name, params);
  if (window.fbq) {
    const mapped = PIXEL_MAP[name] || name;
    const pixelParams = {
      value: params.value,
      currency: params.currency || "EUR",
      content_ids: params.content_ids,
      content_name: params.content_name,
      content_type: params.content_type || "product",
    };
    window.fbq("track", mapped, pixelParams);
  }
  collectFirstParty(name, params);
};

// ---------------------------------------------------------------------------
// GA4 Enhanced Ecommerce — funnel events (VORNX).
// Every helper funnels through trackEvent(), so it is a no-op until the user
// has given cookie consent and GA4 is initialized (GDPR preserved).
// item_id = VORNX SKU; value = items only (price × quantity), NEVER shipping/VAT.
// ---------------------------------------------------------------------------
const resolveName = (n) =>
  typeof n === "string" ? n : (n && (n.it || n.en || Object.values(n)[0])) || "";

const toItem = (src, quantity) => {
  const item = {
    item_id: src.sku || src.slug || src.product_id || src.id || "",
    item_name: resolveName(src.name),
    price: Number(src.price != null ? src.price : src.unit_price) || 0,
    quantity: quantity != null ? quantity : src.quantity || 1,
  };
  if (src.variant_label) item.item_variant = src.variant_label;
  return item;
};

const sumValue = (items) =>
  Math.round(items.reduce((s, i) => s + i.price * i.quantity, 0) * 100) / 100;

export const trackViewItem = (product) => {
  if (!product) return;
  const price = product.price;
  const items = [toItem({ sku: product.sku, slug: product.slug, name: product.name, price }, 1)];
  trackEvent("view_item", { currency: "EUR", value: sumValue(items), items });
};

export const trackAddToCart = (product, quantity = 1, variant = null) => {
  if (!product) return;
  const price = variant && variant.price != null ? variant.price : product.price;
  const items = [
    toItem({ sku: product.sku, slug: product.slug, name: product.name, price, variant_label: variant?.label }, quantity),
  ];
  trackEvent("add_to_cart", { currency: "EUR", value: sumValue(items), items });
};

export const trackBeginCheckout = (cartItems = []) => {
  if (!cartItems.length) return;
  const items = cartItems.map((i) => toItem(i, i.quantity));
  trackEvent("begin_checkout", { currency: "EUR", value: sumValue(items), items });
};

export const trackAddPaymentInfo = (cartItems = [], paymentType = "unknown") => {
  if (!cartItems.length) return;
  const items = cartItems.map((i) => toItem(i, i.quantity));
  trackEvent("add_payment_info", {
    currency: "EUR",
    value: sumValue(items),
    payment_type: paymentType || "unknown",
    items,
  });
};

// Idempotent: a purchase for a given order is reported at most once, even across
// page reloads of the confirmation page (guard keyed on the order id).
export const trackPurchase = (order) => {
  if (!order || !order.id) return;
  const guardKey = `vornx_ga_purchase_${order.id}`;
  try {
    if (localStorage.getItem(guardKey)) return;
  } catch {
    /* localStorage unavailable — still fire, best effort */
  }
  const items = (order.items || []).map((li) =>
    toItem({ sku: li.sku, slug: li.slug, name: li.name, price: li.unit_price, variant_label: li.variant_label }, li.quantity)
  );
  if (!items.length) return;
  trackEvent("purchase", {
    transaction_id: order.order_number || order.id,
    currency: order.currency || "EUR",
    value: sumValue(items),
    shipping: Number(order.shipping) || 0,
    items,
  });
  try {
    localStorage.setItem(guardKey, "1");
  } catch {
    /* ignore */
  }
};
